import React, { useEffect, useMemo, useState } from 'react'
import {
  Divider,
  Typography,
  Link as MuiLink,
  Box,
  ButtonBase,
  Tooltip,
  ClickAwayListener,
} from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'
import AddToCartButton from '../AddToCartButton'
import BuyNowButton from '../BuyNowButton'
import { Link } from '@src/components/Link'
import MaterialAvailabilityPreview from '../MaterialAvailabilityPreview/MaterialAvailabilityPreview'
import LiquidQuantityInputAdapter from '../LiquidQuantityInputAdapter'
import LiquidSelect, {
  liquidSelectOptionRenderer,
} from '@src/components/LiquidSelect'
import { Formik, Field, useFormikContext } from 'formik'
import AddToListButton from '@src/components/AddToListButton'
import { useAddToCartWithGlobalSnackbar } from '@src/utils/useCart'
import { hasMultipleMiniMumOrderQuantityError } from '@src/utils/cartUtils'
import { useNewCart } from '@src/utils/useNewCart'
import ErrorCircleIcon from '@src/icons/ErrorCircleIcon'
import getConfig from 'next/config'
import {
  sendBeginRequestEvent,
  sendPricingAvailabilityInteractionEvent,
} from '@src/utils/analytics/pricingAndAvailability'
import { sendNavigationInteractionEvent } from '@src/utils/analytics'
import { ValidMaterialPricingDetailFragment } from '@src/fragments/ProductPricing.generated'
import { PdpFieldsFragment } from '@src/queries/PDPQuery.generated'
import { ApolloError } from '@apollo/client'
import ProductPriceAvailabilityError from '@src/components/ProductPriceAvailability/ProductPriceAvailabilityError'
import { getSellerName } from '@src/utils/searchUtils'
import PromoBundleButton from '@src/components/PromoBundleButton/PromoBundleButton'
import PromotionalMessage from '@src/components/PromotionalMessage'
import SupplementaryMessage from '@src/components/SupplementaryMessage'
import RestrictedItemModal from '@src/components/RestrictedItemModal'
import LockIcon from '@src/icons/LockIcon'
import {
  SitePreference,
  canUserRequestQuote,
  useCurrentUser,
} from '@src/utils/useCurrentUser'
import RequestQuoteButton from '@src/components/RequestQuoteModal/RequestQuoteButton'
import { setAddToCartData } from '@utils/analytics/cartAndCheckoutEvents'
import { AddToCartPagesEnum } from '@utils/analytics/enums'
import { useUserSession } from '@src/utils/useUserSession'
import { loginRoute, homeRoute, useRouter, support } from '@src/routes'
import { useAbility, Abilities } from '@src/utils/useAbility'
import ExpiryDate from '@src/components/ExpiryDate'
import MaterialTextWithCopy from '../MaterialTextWithCopy/MaterialTextWithCopy'
import HidePriceMessage from '@src/components/HidePriceMessage'
import AdditionalInfoTooltip from './AdditionalInfoTooltip'
import { TrackedAvailabilityMessageProvider } from '@src/components/TrackedAvailabilityMessage/TrackedAvailabilityMessageContext'
import decodeTitle from '@utils/decodeTitle'
import RequestQuoteButtonB2B from '../RequestQuoteModalB2B/RequestQuoteButtonB2B'
import { ReadyToShipNotification } from '@src/components/ProductInfo/ReadyToShipNotification'
import { useProductErpType } from '@src/utils/useProductErpType'
import SignInButton from '@src/components/SignInButton'
import CommonAccordion from '@src/components/Common/CommonAccordion'
import CommonRadio from '@src/components/Common/CommonRadio'
import NewTag from '@src/components/NewTag'
import { useGetSubscriptionFrequenciesQuery } from '@src/queries/SubscriptionFrequencyQuery.generated'
import LinkStyleButton from '@src/components/LinkStyleButton'
import subscriptionFrequencyMessages from '@src/utils/MessageSets/defineSubscriptionFrequencyMessages'
import PriceDisclaimer from './PriceDisclaimer'
import { useCheckUserCountry } from '@src/utils/useCheckUserCountry'
import countriesMap from '@localization/countries.json'
import Promos from './Promos'
import { PromoEligibilityResponse } from '@src/types/graphql-types'
import PromoBadge from '../PromoBadge'
import { PromoType, PromoQualifierType } from '@src/utils/enum'
import { useDecision } from '@optimizely/react-sdk'
import { Linq } from '@src/components/Linq'
import ProductNotSold from '@src/components/ProductPriceAvailability/ProductNotSold'
import JapanMMViewFAQ from '@src/components/JapanMMViewFAQ'
import { useCheckMOQLessThanCountry } from '@utils/useUserCountry'
import { getPromoData } from '@src/utils/promoUtils'
import HandleMarkup from '../HandleMarkup'
import NonGuestShoppingMessage from '@src/components/NonGuestShoppingMessage'

/* eslint-disable sort-keys */
const useStyles = makeStyles()((theme) => ({
  buyBoxContainer: {
    backgroundColor: theme.palette.background.grey,
    boxShadow: '2px 2px 6px 0 rgba(0, 0, 0, 0.25);',
    padding: theme.spacing(4),
  },
  buyBoxContainerNoBoxShadow: {
    boxShadow: 'none',
    padding: theme.spacing(4, 0),
  },
  productName: {
    marginBottom: theme.spacing(-1),
  },
  materialNumber: {
    marginBottom: theme.spacing(-1),
    maxWidth: 200,
  },
  price: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: theme.typography.fontWeightBold,
    marginTop: theme.spacing(1),
    color: theme.palette.orange,
  },
  listPrice: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightLight,
    color: theme.palette.grey[600],
  },
  listPriceLabel: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightLight,
    color: theme.palette.grey[600],
    marginRight: theme.spacing(1),
  },
  saveLabel: {
    color: theme.palette.orange,
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightLight,
    marginLeft: theme.spacing(2),
  },
  ctaLink: {
    display: 'flex',
    justifyContent: 'center',
    fontSize: theme.typography.pxToRem(16),
    margin: theme.spacing(4, 0, 1),
  },
  loaderContainer: {
    position: 'relative',
    backgroundColor: theme.palette.background.default,
  },
  // 6 frames * 0.15 seconds = 0.9 seconds total
  loader: {
    animation: '$fadeAnimation 0.9s linear infinite',
    backgroundColor: '#efefef',
    minHeight: 424,
  },
  // 0.15 seconds for each frame
  '@keyframes fadeAnimation': {
    '0%, 100%': { opacity: 0.4 },
    '16.67%': { opacity: 0.6 },
    '33.33%': { opacity: 0.8 },
    '50%': { opacity: 1 },
    '66.67%': { opacity: 0.8 },
    '83.33%': { opacity: 0.6 },
  },
  vendorSku: {
    fontSize: theme.typography.pxToRem(14),
    margin: theme.spacing(1, 0),
  },
  errorIcon: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.error.main,
    margin: theme.spacing(1, 1, 0, 0),
  },
  phone: {
    display: 'inline-block',
  },
  or: {
    margin: theme.spacing(0, 1),
  },
  email: {
    display: 'inline-block',
    wordBreak: 'break-word',
  },
  linkStyle: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.primary.main,
  },
  subscriptionText: {
    marginLeft: theme.spacing(8),
    fontWeight: theme.typography.fontWeightMedium,
  },
  subscriptionError: {
    fontSize: theme.typography.pxToRem(12),
    display: 'flex',
    alignItems: 'flex-end',
  },
  productBlockedLink: {
    margin: theme.spacing(4, 0),
    textAlign: 'center',
  },
  lockIcon: {
    color: theme.palette.primary.main,
    fontSize: theme.typography.pxToRem(16),
  },
  productBlockedText: {
    color: theme.palette.primary.main,
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightBold,
    marginLeft: theme.spacing(2),
  },
  labelFontSize: {
    fontSize: theme.typography.pxToRem(14),
  },
  minimumQuantityError: {
    color: theme.palette.error.main,
    fontWeight: 'bold',
  },
  pricingModal: {
    display: 'flex',
    justifyContent: 'center',
  },
  hideLink: {
    display: 'none',
  },
  minHeight: {
    minHeight: 475,
  },
  minHeightError: {
    minHeight: 320,
    padding: theme.spacing(15, 12, 0, 3),
  },
  inquireLink: {
    display: 'flex',
    minWidth: 0,
    padding: 0,
    fontSize: theme.typography.pxToRem(16),
    fontWeight: theme.typography.fontWeightMedium,
  },
  subscriptionContainer: {
    '& .MuiAccordion-root': {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      '& .MuiSelect-root': {
        padding: theme.spacing(1, 7, 1, 2),
      },
    },
    backgroundColor: theme.palette.background.grey,
    margin: theme.spacing(-3, -4, -4),
  },
  accordion: {
    backgroundColor: theme.palette.background.grey,
  },
  summary: {
    width: '100%',
    padding: theme.spacing(0),
    '& .MuiAccordionSummary-content.Mui-expanded': {
      margin: theme.spacing(4, 0),
    },
    '&.Mui-focused, &.Mui-focusVisible': {
      backgroundColor: 'transparent',
    },
    '& .MuiFormControlLabel-root': {
      marginRight: theme.spacing(0),
    },
  },
  details: {
    margin: theme.spacing(0),
    padding: theme.spacing(0),
    display: 'inherit',
  },
  summaryContainer: {
    width: '100%',
  },
  summaryLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: theme.spacing(0, 4, 0, 0),

    '& .MuiFormControlLabel-root': {
      marginBottom: 0,
    },
  },
  accordionBackground: {
    backgroundColor: '#ECECF4',
  },
  infoLink: {
    marginLeft: theme.spacing(8),
    fontWeight: 700,
    color: theme.palette.primary.main,
    display: 'inline-block',
  },
  tooltipHeading: {
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    fontSize: theme.typography.pxToRem(16),
  },
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
    maxWidth: theme.typography.pxToRem(408),
    fontSize: theme.typography.pxToRem(14),
    boxShadow: '0 6px 13px 0 rgba(0, 0, 0, 0.16)',
    padding: theme.spacing(4),
  },
  arrow: {
    '&:before': {
      border: `solid ${theme.palette.grey[500]} 1px`,
    },
    color: theme.palette.common.white,
    fontSize: theme.typography.pxToRem(24),
  },
  badgeWrapper: {
    paddingTop: theme.spacing(2),
  },
  subsSaveMessage: {
    color: theme.palette.error.main,
    fontSize: theme.typography.pxToRem(14),
    marginLeft: theme.spacing(8),
  },
  signInOffer: {
    '& p': {
      fontSize: theme.typography.pxToRem(14),
    },
    paddingTop: theme.spacing(2),
  },
  // REMOVE ONCE AB TESTING FOR TICKET FIND-1465 IS COMPLETED
  abTestAddToCartButton: {
    backgroundColor: theme.palette.vibrant.yellow,
    color: theme.palette.secondary.main,
    '&:hover': {
      backgroundColor: theme.palette.warning.main,
      color: theme.palette.secondary.main,
    },
    '&:focus': {
      backgroundColor: theme.palette.vibrant.yellow,
      border: `${theme.spacing(0.75)} solid ${theme.palette.common.white}`,
      color: theme.palette.secondary.main,
      outline: `${theme.spacing(0.75)} solid ${theme.palette.warning.main}`,
    },
  },
  packSize: {
    padding: theme.spacing(1, 0, 2, 0),
  },
  mobileSrpMaterialNumber: {
    maxWidth: 'none',
    paddingRight: theme.spacing(3),
    fontSize: theme.typography.pxToRem(12),
  },
  mobileSrpPrice: {
    marginTop: theme.spacing(0),
    display: 'inline-flex',
    paddingRight: theme.spacing(2),
  },
  mobileSrpStrikePrice: {
    verticalAlign: 'top',
  },
  mobileSrpSaveLabel: {
    display: 'flex',
    marginLeft: theme.spacing(0),
    marginTop: theme.spacing(2),
  },
  fieldHeight: {
    height: 40,
  },
}))

const {
  publicRuntimeConfig: { featureFlags },
} = getConfig()

interface OrderFormValues {
  quantity: number
  materialNumber?: string
}
interface BuyBoxProps {
  isMultiCountryUser: boolean
  countryCode: string
  loading: boolean
  material: ValidMaterialPricingDetailFragment | null
  product: Partial<PdpFieldsFragment>
  error?: ApolloError | undefined
  isPricingModal?: boolean
  showPriceDisclaimer?: boolean
  focus?: string
  promos?: PromoEligibilityResponse | null
  shouldShowReadyToShipNotification?: boolean
  sourceString?: string
  productNotSoldInCountry?: boolean
  isMobileSrpBuyBox?: boolean
}

const BuyBox: React.FC<BuyBoxProps> = ({
  isMultiCountryUser,
  countryCode,
  loading,
  material,
  product,
  error,
  isPricingModal = false,
  showPriceDisclaimer = false,
  shouldShowReadyToShipNotification = false,
  focus,
  sourceString,
  promos,
  productNotSoldInCountry,
  isMobileSrpBuyBox,
}) => {
  const { classes, cx } = useStyles()
  const router = useRouter()
  const { userSession } = useUserSession()
  const isMOQLessThanCountry = useCheckMOQLessThanCountry()
  const user = useCurrentUser()
  const {
    userIsLoggedIn,
    currentUser,
    isPublicOrderingCustomer,
    isSubscriptionUser,
    isQuoteRequester,
    isB2BUser,
    getSitePreference,
    isDarmstadtUser,
    isPipelineUser,
    isFTBUser,
  } = user
  const hideAddToCartForPrepackItems = getSitePreference(
    SitePreference.HideAddToCartForPrepackItems
  )
  const shouldRenderRequestQuoteButton = canUserRequestQuote(user)
  const { authorized } = useAbility(Abilities.canAddToCart)
  const addToCart = useAddToCartWithGlobalSnackbar()
  const { shouldUseNewCart, addToCart: addToNewCart } = useNewCart()
  const { formatMessage } = useIntl()
  const [restrictedItemModalOpen, setRestrictedItemModalOpen] = useState(false)
  const [isMounted, setIsMounted] = useState(false)
  const [subscription, setSubscription] = useState(false)
  const isSubscriptionDisabled = isFTBUser || isB2BUser || isPipelineUser
  const promo = useMemo(
    () =>
      promos?.skus?.find(
        (promoEntry) => promoEntry.sku === material?.materialNumber
      ),
    [promos, material]
  )

  const buyBoxPromoData = getPromoData(promo)

  const promoType = buyBoxPromoData?.promoType
  const promoCode = buyBoxPromoData?.promoCode
  const promoDiscount = buyBoxPromoData?.discount
  const qualifierType = buyBoxPromoData?.qualifierType
  const hidePromoDetailsForAnonUser =
    buyBoxPromoData?.hidePromoDetailsForAnonUser

  const requireLoginToSeePromo =
    (hidePromoDetailsForAnonUser ?? false) && !userIsLoggedIn
  const listPrice = material?.listPrice ?? 0
  const price = material?.price ?? 0
  const isSameCurrency =
    material && material?.currency === material?.listPriceCurrency
  //show list price only if it's greater than customer price
  const discount =
    listPrice > price ? ((listPrice - price) / listPrice) * 100 : null
  const bulkOrderCta = product.forms?.find((form) => form.type === 'bulk order')
  const allSecondaryCtas =
    product.forms?.filter((form) => form.type !== 'bulk order') ?? []
  const secondaryCtas = useMemo(() => {
    if (bulkOrderCta) {
      return allSecondaryCtas.slice(0, 1)
    }
    return allSecondaryCtas.slice(0, 2)
  }, [allSecondaryCtas, bulkOrderCta])

  const [openTooltip, setOpenTooltip] = useState(false)
  const handleTooltipClose = () => {
    setOpenTooltip(false)
  }

  const handleTooltipOpen = () => {
    setOpenTooltip(true)
    sendPricingAvailabilityInteractionEvent({
      action: 'learn more subscription order',
      section: 'pricing card',
      component: 'right rail',
      elementType: 'link',
      elementText: 'learn more',
      material: {
        materialDescription: material?.materialDescription,
        brand: material?.brand,
        product: material?.product,
        materialNumber: material?.materialNumber,
      },
    })
  }

  const isSubscriptionOrderingEnabled =
    !isMobileSrpBuyBox &&
    featureFlags.subscriptions &&
    isSubscriptionUser &&
    material?.shipsToday

  const isBBE = material?.catalogType === 'buildingblocks'
  const isMarketplace = product?.isMarketplace
  const [firstAvailability] = material?.availabilities ?? []
  const { contactPhone, contactEmail } = firstAvailability?.contactInfo ?? {}
  const hasInquireLink = material?.availabilities?.some(
    (item) => item?.displayInquireLink
  )
  const canAddToCart =
    authorized &&
    !hideAddToCartForPrepackItems &&
    !isMultiCountryUser &&
    (!isMarketplace ||
      (isMarketplace && !!currentUser?.metadata?.isMarketplaceCartEnabled))
  const showInquireMessage =
    firstAvailability?.key === 'INQUIRE_FOR_PRICE_AND_AVAILABILITY'
  const guestShowLocalDealer =
    currentUser?.__typename === 'GuestUser' && !canAddToCart
  const loggedInShowLocalDealer =
    currentUser?.__typename === 'LoggedInUser' &&
    !currentUser?.metadata?.guestShoppingType &&
    !isPublicOrderingCustomer &&
    !isQuoteRequester
  const isBuySaveBadgeVisible =
    promoType === PromoType.BuySave &&
    (qualifierType === PromoQualifierType.Currency ||
      qualifierType === PromoQualifierType.Quantity)

  const userEmail = user.currentUser?.email || ''
  const productImageUrl = product?.images?.[0]?.smallUrl || ''
  const productNumber = product.productNumber || ''

  const { isPurpleProduct } = useProductErpType(product.erp_type)
  const isChinaUser = useCheckUserCountry(countriesMap.CN.countryCode)
  const requiresSignInForBestPrice =
    isPurpleProduct && isChinaUser && !userIsLoggedIn

  // Renders info icon if there is additional info.
  const hasAdditionalInfoFields =
    !isMobileSrpBuyBox &&
    Object.values(material?.additionalInfo ?? {}).filter(
      (field) => !!field && field !== 'CartAdditionalInfo'
    ).length

  const toggleRestrictedItemModalOpen = () => {
    setRestrictedItemModalOpen((open) => !open)
  }

  const formatFrequencyLabel = (code: string) => {
    const frequencyMessage = subscriptionFrequencyMessages[code]
    return frequencyMessage ? formatMessage(frequencyMessage) : code
  }

  const [selectedFrequency, setSelectedFrequency] = useState('FOUR_WEEKS')
  const { data: frequencyData, error: frequencyError } =
    useGetSubscriptionFrequenciesQuery({
      skip: !isSubscriptionOrderingEnabled,
      ssr: false,
    })

  const frequencyOptions =
    frequencyData?.subscriptionFrequencies?.map((freq) => ({
      label: formatFrequencyLabel(freq),
      value: freq,
    })) || []

  const handleAddToCart = async (material, quantity: number) => {
    let appliedPromoCode: string | null = null

    // For subscription orders, only pass promocode if it's SUBSCRIBE_SAVE type
    if (subscription && promoType === PromoType.SubscribeSave && promoCode) {
      appliedPromoCode = promoCode
    }

    setAddToCartData(
      [
        {
          materialNumber: material.materialNumber,
          quantity,
          marketplaceOfferId: material.marketplaceOfferId,
          subscriptionFrequency: subscription ? selectedFrequency : null,
        },
      ],
      [material],
      isPricingModal
        ? AddToCartPagesEnum.SearchResultPage
        : AddToCartPagesEnum.ProductDetailPage
    )

    if (shouldUseNewCart) {
      await addToNewCart([
        {
          materialNumber: material.materialNumber,
          quantity,
        },
      ])
    } else {
      await addToCart(
        [
          {
            materialNumber: material.materialNumber,
            quantity,
            subscriptionFrequency: subscription ? selectedFrequency : null,
            promoCode: appliedPromoCode,
            listPrice,
          },
        ],
        undefined,
        undefined,
        isMarketplace
      )
    }

    setSubscription(false)
    setSelectedFrequency('FOUR_WEEKS')
  }

  const handleOrderTypeChange = (isSubscription: boolean) => {
    setSubscription(isSubscription)
    sendPricingAvailabilityInteractionEvent({
      action: isSubscription
        ? 'subscription order radio'
        : 'one time order radio',
      section: 'pricing card',
      component: 'right rail',
      elementType: 'radio button',
      material: {
        brand: material?.brand,
        materialDescription: material?.materialDescription,
        materialNumber: material?.materialNumber,
        product: material?.product,
      },
    })
  }

  const handleFrequencyChange = (frequency: string) => {
    const frequencyLabel = formatFrequencyLabel(frequency)

    setSelectedFrequency(frequency)
    sendPricingAvailabilityInteractionEvent({
      action: 'subscription frequency selection',
      detail: frequencyLabel.toLowerCase(),
      section: 'pricing card',
      component: 'right rail',
      elementType: 'drop down',
      elementText: frequencyLabel.toLowerCase(),
      material: {
        brand: material?.brand,
        materialDescription: material?.materialDescription,
        materialNumber: material?.materialNumber,
        product: material?.product,
      },
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' && !isSubscriptionDisabled) {
      const elementID = (e.target as HTMLElement).closest('[id]')
      const isOneTimeOrderHeader = elementID?.id === 'one-time-order-header'
      setSubscription(!isOneTimeOrderHeader)
    }
  }

  // REVERT BACK TO ORIGINAL CODE ONCE AB TEST FOR FIND-1465 IS COMPLETED
  const AbTestAddToCartButton = ({ material, quantity, validQty }) => {
    const { classes } = useStyles()
    const [decision] = useDecision('wabt-233b')

    return (
      <Box mb={2} mt={2}>
        <AddToCartButton
          canAddToCart={canAddToCart}
          dirty
          fullWidth
          handleAddToCart={() => handleAddToCart(material, quantity)}
          hideAddToCartForPrepackItems={hideAddToCartForPrepackItems}
          isSubmitting={false}
          size="large"
          disabled={
            !validQty ||
            material?.isBlockedProduct ||
            material?.availableQtyInStock === 0 ||
            hasInquireLink
          }
          addToCartButtonClass={cx(
            classes.fieldHeight,
            decision?.enabled ? classes.abTestAddToCartButton : ''
          )}
        />
      </Box>
    )
  }

  const OneTimeOrder = ({ validQty }: { validQty: boolean }) => {
    const { values } = useFormikContext<OrderFormValues>()
    const { quantity } = values

    // If promoType is SubscribeSave, default to subscription
    useEffect(() => {
      if (!isMounted && promoType === PromoType.SubscribeSave) {
        setSubscription(true)
      }
      setIsMounted(true)
    }, [promoType])

    return (
      <>
        {material?.promotionalMessage ? (
          <Box mb={3}>
            <Typography variant="body2">
              <PromotionalMessage promotional={material?.promotionalMessage} />
            </Typography>
          </Box>
        ) : null}

        {material?.isBlockedProduct ? (
          <Box className={classes.productBlockedLink}>
            <ButtonBase onClick={() => setRestrictedItemModalOpen(true)}>
              <LockIcon className={classes.lockIcon} />
              <Typography className={classes.productBlockedText}>
                <FormattedMessage
                  id="PRODUCT_BLOCKED"
                  defaultMessage="Product Blocked"
                />
              </Typography>
            </ButtonBase>
            {restrictedItemModalOpen && (
              <RestrictedItemModal
                open={restrictedItemModalOpen}
                onCancel={toggleRestrictedItemModalOpen}
              />
            )}
          </Box>
        ) : (
          <Field
            inputProps={{
              inputMode: 'numeric',
            }}
            name="quantity"
            component={LiquidQuantityInputAdapter}
            disabled={hideAddToCartForPrepackItems}
            error={!validQty}
            filled
            max={material?.availableQtyInStock ?? 9999}
            min={1}
            className={classes.fieldHeight}
          />
        )}
        {requiresSignInForBestPrice ? (
          <Box mb={2} mt={2}>
            <SignInButton fullWidth size="large" />
          </Box>
        ) : (
          <AbTestAddToCartButton
            material={material}
            quantity={quantity}
            validQty={validQty}
          />
        )}

        {material?.isBuyNow && canAddToCart ? (
          <BuyNowButton
            fullWidth
            materialNumber={material?.materialNumber}
            quantity={quantity}
            marketplace={isMarketplace}
            gaType={
              isPricingModal ? 'search results page' : 'product detail page'
            }
            promoCode={
              promoType && promoType === PromoType.PercentOff
                ? promoCode
                : undefined
            }
          />
        ) : null}

        {shouldRenderRequestQuoteButton &&
        featureFlags?.requestQuoteModal &&
        !hideAddToCartForPrepackItems &&
        !isMarketplace &&
        !isBBE ? (
          <Box mt={2}>
            {isB2BUser ? (
              <RequestQuoteButtonB2B
                quoteData={{
                  material,
                  product,
                  quantity,
                }}
                disabled={!quantity}
              />
            ) : (
              <RequestQuoteButton
                quoteData={{
                  material,
                  product,
                  quantity,
                }}
                disabled={!quantity}
              />
            )}
          </Box>
        ) : null}

        {bulkOrderCta &&
          !isMarketplace &&
          !hideAddToCartForPrepackItems &&
          !isDarmstadtUser && (
            <Link
              href={`${bulkOrderCta.url}?tfa_1=${
                product.productNumber
              }&tfa_2=${decodeTitle(product?.name ?? '')}`}
              passHref
            >
              <MuiLink
                className={classes.ctaLink}
                id="bulk-order-cta"
                color="primary"
                target="_blank"
                onClick={() =>
                  sendBeginRequestEvent({
                    product,
                    materialNumber: material?.materialNumber,
                    cta: bulkOrderCta,
                  })
                }
              >
                <FormattedMessage
                  id="REQUEST_BULK_ORDER"
                  defaultMessage="Request a Bulk Order"
                />
              </MuiLink>
            </Link>
          )}
        {Boolean(secondaryCtas.length) &&
          !isMarketplace &&
          secondaryCtas.map((secondaryCta) => (
            <Link href={secondaryCta.url} key={secondaryCta.label} passHref>
              <MuiLink
                className={classes.ctaLink}
                color="primary"
                target="_blank"
                onClick={() =>
                  sendBeginRequestEvent({
                    product,
                    materialNumber: material?.materialNumber,
                    cta: secondaryCta,
                  })
                }
              >
                <FormattedMessage
                  id={secondaryCta.label.toUpperCase().split(' ').join('_')}
                  defaultMessage={secondaryCta.label}
                />
              </MuiLink>
            </Link>
          ))}
        {shouldShowReadyToShipNotification && (
          <ReadyToShipNotification
            material={material}
            userEmail={userEmail}
            productImageUrl={productImageUrl}
            productNumber={productNumber}
          />
        )}
      </>
    )
  }

  const SubscriptionOrder = ({ validQty }: { validQty: boolean }) => {
    const { values } = useFormikContext<OrderFormValues>()
    const { quantity } = values

    return (
      <>
        {isPipelineUser ? (
          <div className={classes.subscriptionText}>
            <FormattedMessage
              id="PIPELINE_USER_SUBSCRIPTIONS_MESSAGE"
              defaultMessage="Subscriptions are not available for customers requiring permission to purchase."
            />
          </div>
        ) : isB2BUser ? (
          <div className={classes.subscriptionText}>
            <FormattedMessage
              id="B2B_USER_SUBSCRIPTIONS_MESSAGE"
              defaultMessage="Subscriptions are not available for eProcurement customers at this time."
            />
          </div>
        ) : isFTBUser ? (
          <div className={classes.subscriptionText}>
            <FormattedMessage
              id="FTB_USER_SUBSCRIPTIONS_MESSAGE"
              defaultMessage="Subscriptions are only available to customers who have previously purchased at this time."
            />
          </div>
        ) : (
          <>
            {material?.isBlockedProduct ? (
              <Box className={classes.productBlockedLink}>
                <ButtonBase onClick={() => setRestrictedItemModalOpen(true)}>
                  <LockIcon className={classes.lockIcon} />
                  <Typography className={classes.productBlockedText}>
                    <FormattedMessage
                      id="PRODUCT_BLOCKED"
                      defaultMessage="Product Blocked"
                    />
                  </Typography>
                </ButtonBase>
                {restrictedItemModalOpen && (
                  <RestrictedItemModal
                    open={restrictedItemModalOpen}
                    onCancel={toggleRestrictedItemModalOpen}
                  />
                )}
              </Box>
            ) : (
              <Field
                inputProps={{
                  inputMode: 'numeric',
                }}
                name="quantity"
                component={LiquidQuantityInputAdapter}
                disabled={hideAddToCartForPrepackItems}
                error={!validQty}
                filled
                max={material?.availableQtyInStock ?? 9999}
                min={1}
                className={classes.fieldHeight}
              />
            )}

            <Box mb={2} mt={2}>
              <LiquidSelect
                value={selectedFrequency}
                onChange={(e) =>
                  handleFrequencyChange(e.target.value as string)
                }
                renderValue={(value) => formatFrequencyLabel(value as string)}
                displayEmpty
                size="medium"
              >
                {frequencyOptions.map((option) =>
                  liquidSelectOptionRenderer(
                    option.value,
                    option.value,
                    option.label
                  )
                )}
              </LiquidSelect>
            </Box>

            <Box mb={2} mt={2}>
              <AddToCartButton
                canAddToCart={canAddToCart}
                dirty
                fullWidth
                handleAddToCart={() => handleAddToCart(material, quantity)}
                hideAddToCartForPrepackItems={hideAddToCartForPrepackItems}
                isSubmitting={false}
                size="large"
                disabled={
                  !validQty ||
                  material?.isBlockedProduct ||
                  material?.availableQtyInStock === 0 ||
                  hasInquireLink
                }
                addToCartButtonClass={classes.fieldHeight}
              />
            </Box>

            {material?.isBuyNow && canAddToCart ? (
              <BuyNowButton
                fullWidth
                materialNumber={material?.materialNumber}
                quantity={quantity}
                marketplace={isMarketplace}
                subscriptionFrequency={selectedFrequency}
                promoCode={
                  promoType === PromoType.SubscribeSave ? promoCode : undefined
                }
                gaType={
                  isPricingModal ? 'search results page' : 'product detail page'
                }
              />
            ) : null}
          </>
        )}
      </>
    )
  }

  const materialWithPromo = {
    ...material,
    discoMessage: formatMessage(
      {
        id: 'DISCO_PROMO_MESSAGE',
        defaultMessage: '{promoDiscount} with code {promoCode}',
      },
      { promoDiscount: `-${promoDiscount}%`, promoCode }
    ),
  }

  const promosGaAvailability =
    material?.availabilities?.find((item) => item?.messageType === 'primary') ??
    material?.availabilities?.[0]

  const promosGaMessageId = promosGaAvailability?.key
  const showPromo =
    promoCode &&
    promoDiscount &&
    promoType !== PromoType.SubscribeSave &&
    promoType === PromoType.PercentOff &&
    !subscription

  if (productNotSoldInCountry) {
    return (
      <div
        className={cx(classes.buyBoxContainer, {
          [classes.buyBoxContainerNoBoxShadow]: isMobileSrpBuyBox,
        })}
      >
        <ProductNotSold productNumber={productNumber} />
      </div>
    )
  }

  return (
    <Formik
      initialValues={{
        materialNumber: material?.materialNumber,
        quantity: 1,
      }}
      onSubmit={(_, actions) => {
        setTimeout(() => {
          actions.setSubmitting(false)
        }, 1000)
      }}
    >
      {({ values }) => {
        const multipleMinimumOrderError = hasMultipleMiniMumOrderQuantityError(
          values.quantity,
          material?.minOrderQuantity,
          isMOQLessThanCountry
        )
        const validQty = values.quantity > 0 && !multipleMinimumOrderError

        return (
          <>
            {loading ? (
              <div className={classes.loaderContainer}>
                <div
                  className={cx(classes.loader, {
                    [classes.minHeight]: isPricingModal,
                  })}
                />
              </div>
            ) : (
              <div
                className={cx(classes.buyBoxContainer, {
                  [classes.minHeight]: isPricingModal,
                  [classes.minHeightError]: isPricingModal && !!error,
                  [classes.buyBoxContainerNoBoxShadow]: isMobileSrpBuyBox,
                })}
              >
                {error ? (
                  <TrackedAvailabilityMessageProvider
                    source="pdp"
                    brand={material?.brand || product?.brand?.name || ''}
                    item={material?.materialNumber || product?.productNumber}
                    waitUntilVisible
                  >
                    <ProductPriceAvailabilityError
                      error={error}
                      productNumber={product?.productNumber}
                      isCenterJustified={false}
                      isMarketplace={isMarketplace}
                      sellerName={
                        product?.displaySellerName ||
                        getSellerName(product?.attributes)
                      }
                      marketplaceSellerId={product?.marketplaceSellerId ?? ''}
                      marketplaceOfferId={product?.marketplaceOfferId ?? ''}
                    />
                  </TrackedAvailabilityMessageProvider>
                ) : (
                  <>
                    <Typography
                      component={isMobileSrpBuyBox ? 'p' : 'h2'}
                      variant={isMobileSrpBuyBox ? 'body1' : 'h2'}
                      className={
                        isMobileSrpBuyBox
                          ? classes.mobileSrpMaterialNumber
                          : classes.materialNumber
                      }
                    >
                      {material ? (
                        <MaterialTextWithCopy
                          materialNumber={material.materialNumber}
                          isShowIcon={isMobileSrpBuyBox}
                        />
                      ) : null}
                      {hasAdditionalInfoFields ? (
                        <AdditionalInfoTooltip material={material} />
                      ) : null}
                    </Typography>

                    {isMobileSrpBuyBox && (
                      <Typography
                        component={'h2'}
                        variant={'h2'}
                        className={classes.packSize}
                      >
                        {material && (
                          <HandleMarkup value={material.packageSize} />
                        )}
                      </Typography>
                    )}

                    {isBBE && material?.vendorSKU ? (
                      <Typography className={classes.vendorSku}>
                        <FormattedMessage
                          id="VENDOR_SKU"
                          defaultMessage="Vendor SKU"
                        />
                        : {material?.vendorSKU}
                      </Typography>
                    ) : null}

                    {showInquireMessage ? (
                      <Box mt={4}>
                        <Typography variant="body2">
                          <ErrorCircleIcon className={classes.errorIcon} />
                          <FormattedMessage
                            id="INQUIRE_FOR_PRICE"
                            defaultMessage="This item is not able to be purchased as a guest user. For further information, please contact us at {contactInfo}."
                            values={{
                              contactInfo: (
                                <>
                                  {contactPhone && (
                                    <span className={classes.phone}>
                                      {contactPhone}
                                    </span>
                                  )}
                                  {contactPhone && contactEmail && (
                                    <Typography
                                      component="span"
                                      className={classes.or}
                                    >
                                      <FormattedMessage
                                        id="OR"
                                        defaultMessage="or"
                                      />
                                    </Typography>
                                  )}
                                  {contactEmail && (
                                    <div className={classes.email}>
                                      <a href={`mailto:${contactEmail}`}>
                                        {contactEmail}
                                      </a>
                                    </div>
                                  )}
                                </>
                              ),
                            }}
                          />
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        {hasInquireLink ? (
                          <Linq
                            href={support.customerSupportWithFormId('tfa_11')}
                            className={classes.inquireLink}
                          >
                            <FormattedMessage
                              id="INQUIRE"
                              defaultMessage="Inquire"
                            />
                          </Linq>
                        ) : (
                          <>
                            {material?.hidePriceMessageKey ? (
                              <Box mt={1}>
                                <HidePriceMessage
                                  msgKey={material?.hidePriceMessageKey}
                                />
                              </Box>
                            ) : (
                              <Typography
                                className={cx(classes.price, {
                                  [classes.mobileSrpPrice]: isMobileSrpBuyBox,
                                })}
                                component={'h3'}
                                variant={'h3'}
                              >
                                <FormattedNumber
                                  style="currency"
                                  currencyDisplay="symbol"
                                  currency={material?.currency}
                                  value={price ?? 0}
                                />
                              </Typography>
                            )}
                            {discount !== null &&
                            discount > 0 &&
                            !material?.hidePriceMessageKey ? (
                              <>
                                <span
                                  className={cx({
                                    [classes.mobileSrpStrikePrice]:
                                      isMobileSrpBuyBox,
                                  })}
                                >
                                  <Typography
                                    component="span"
                                    className={classes.listPriceLabel}
                                  >
                                    <FormattedMessage
                                      id="LIST_PRICE"
                                      defaultMessage="List Price"
                                    />
                                  </Typography>

                                  <Typography
                                    component="span"
                                    className={classes.listPrice}
                                  >
                                    <FormattedNumber
                                      style="currency"
                                      currencyDisplay="symbol"
                                      currency={
                                        material?.listPriceCurrency ||
                                        material?.currency
                                      }
                                      value={listPrice ?? 0}
                                    />
                                  </Typography>
                                </span>
                                {/* should display Save % only if discount is >= 5% */}
                                {isSameCurrency && discount >= 5 && (
                                  <Typography
                                    component="span"
                                    className={cx(classes.saveLabel, {
                                      [classes.mobileSrpSaveLabel]:
                                        isMobileSrpBuyBox,
                                    })}
                                  >
                                    <FormattedMessage
                                      id="SAVE_DISCOUNT_PRICE"
                                      defaultMessage="Save {discount}%"
                                      values={{
                                        discount: Math.round(discount),
                                      }}
                                    />
                                  </Typography>
                                )}
                              </>
                            ) : null}
                          </>
                        )}

                        {showPriceDisclaimer && (
                          <PriceDisclaimer
                            countryCode={userSession.country}
                            hasDiscount={
                              discount !== null &&
                              !!material?.ecomStrikeThroughPrice
                            }
                            isBBE={isBBE}
                          />
                        )}

                        {requireLoginToSeePromo ? (
                          <div className={classes.signInOffer}>
                            <Linq
                              href={`${loginRoute.index()}?redirect=${encodeURIComponent(
                                router.asPath || homeRoute.index()
                              )}`}
                              onClick={() =>
                                sendNavigationInteractionEvent({
                                  action: 'cta click',
                                  detail: 'visit sign in page',
                                  section: 'pricing card',
                                  component: 'right rail',
                                  elementType: 'link',
                                  elementText: 'sign in',
                                  linkUrl: `${loginRoute.index()}?redirect=${encodeURIComponent(
                                    router.asPath || homeRoute.index()
                                  )}`,
                                  productId: material?.product,
                                  productVariant: material?.materialNumber,
                                  productBrand: material?.brand,
                                })
                              }
                            >
                              <FormattedMessage
                                id="SIGN_IN_FOR_BEST_PRICE"
                                defaultMessage="Sign In for Best Price"
                              />
                            </Linq>
                          </div>
                        ) : isBuySaveBadgeVisible ? (
                          <PromoBadge
                            className={classes.badgeWrapper}
                            showTooltip
                            promoData={buyBoxPromoData}
                            materialNumber={material?.materialNumber}
                            product={product}
                          />
                        ) : (
                          <TrackedAvailabilityMessageProvider
                            source="pdp"
                            brand={material?.brand}
                            item={material?.materialNumber}
                            pricing={materialWithPromo}
                          >
                            {showPromo && (
                              <Promos
                                availability={promosGaAvailability}
                                gaMessageId={promosGaMessageId}
                                promoCode={promoCode}
                                promoDiscount={promoDiscount}
                              />
                            )}
                          </TrackedAvailabilityMessageProvider>
                        )}

                        {!isMobileSrpBuyBox && (
                          <Box mt={4}>
                            <Divider />
                          </Box>
                        )}

                        <Box
                          mt={isMobileSrpBuyBox ? 2 : 4}
                          mb={2}
                          data-optimizely="availability"
                        >
                          {material ? (
                            showPromo ? (
                              <>
                                <MaterialAvailabilityPreview
                                  material={material}
                                  canAddToCart={canAddToCart}
                                  countryCode={countryCode}
                                  gaDetailsClick={() =>
                                    sendPricingAvailabilityInteractionEvent({
                                      action: 'check availability details',
                                      section: isPricingModal
                                        ? 'modal p&a'
                                        : 'summary',
                                      component: isPricingModal
                                        ? 'modal'
                                        : 'right rail',
                                      elementType: 'link',
                                      elementText: 'details',
                                      material,
                                    })
                                  }
                                  gaSection={'pricing card'}
                                  gaComponent={'right rail'}
                                />
                                <JapanMMViewFAQ />
                              </>
                            ) : (
                              <TrackedAvailabilityMessageProvider
                                source={
                                  isPricingModal
                                    ? sourceString
                                      ? sourceString
                                      : `srp - ${focus}`
                                    : 'pdp'
                                }
                                brand={material.brand}
                                item={material.materialNumber}
                                pricing={material}
                                waitUntilVisible
                              >
                                <MaterialAvailabilityPreview
                                  material={material}
                                  canAddToCart={canAddToCart}
                                  countryCode={countryCode}
                                  gaDetailsClick={() =>
                                    sendPricingAvailabilityInteractionEvent({
                                      action: 'check availability details',
                                      section: isPricingModal
                                        ? 'pricing card'
                                        : 'summary',
                                      component: 'right rail',
                                      elementType: 'link',
                                      elementText: 'details',
                                      material,
                                    })
                                  }
                                  gaSection={'pricing card'}
                                  gaComponent={'right rail'}
                                />
                                <JapanMMViewFAQ />
                              </TrackedAvailabilityMessageProvider>
                            )
                          ) : null}

                          {material?.expirationDate ? (
                            <Box mt={1}>
                              <ExpiryDate
                                expirationDate={material.expirationDate}
                                outerClassName={classes.labelFontSize}
                                displayInline
                                displayColon
                              />
                            </Box>
                          ) : null}

                          {material?.minOrderQuantity &&
                          material?.minOrderQuantity > 1 ? (
                            <Typography
                              className={cx(classes.labelFontSize, {
                                [classes.minimumQuantityError]: !validQty,
                              })}
                            >
                              <FormattedMessage
                                id="MINIMUM_ORDER_QUANTITY"
                                defaultMessage="Minimum Order Quantity: "
                              />
                              {material.minOrderQuantity}
                            </Typography>
                          ) : null}

                          {!isMobileSrpBuyBox &&
                          firstAvailability?.supplementaryMessage
                            ?.messageKey ? (
                            <Box mt={1}>
                              <SupplementaryMessage
                                supplementary={
                                  firstAvailability?.supplementaryMessage
                                }
                                productName={product.name}
                                productId={material?.product}
                                productBrand={material?.brand}
                                productVariant={material?.materialNumber}
                              />
                            </Box>
                          ) : null}

                          {material?.isPBAvailable && (
                            <Box mt={2}>
                              <PromoBundleButton
                                materialNumber={material?.materialNumber ?? ''}
                                brand={material?.brand}
                                productName={material?.product ?? ''}
                                erp_type={product?.erp_type}
                              />
                            </Box>
                          )}
                        </Box>

                        {!isMobileSrpBuyBox && (
                          <Box mt={3}>
                            <Divider />
                          </Box>
                        )}

                        {isSubscriptionOrderingEnabled ? (
                          <Box className={classes.subscriptionContainer}>
                            <CommonAccordion
                              details={<OneTimeOrder validQty={validQty} />}
                              expanded={!subscription}
                              onKeyDown={handleKeyDown}
                              summary={
                                <CommonRadio
                                  label="One Time Order"
                                  name="defaultName"
                                  size="medium"
                                  value="defaultValue"
                                  onChange={() => handleOrderTypeChange(false)}
                                  checked={!subscription}
                                />
                              }
                              summaryAriaControls="one-time-order-content"
                              summaryId="one-time-order-header"
                              overrideClasses={{
                                accordion: classes.accordion,
                                summary: classes.summary,
                                details: classes.details,
                              }}
                            />
                            <CommonAccordion
                              details={
                                <SubscriptionOrder validQty={validQty} />
                              }
                              expanded={
                                isFTBUser || isB2BUser || isPipelineUser
                                  ? true
                                  : subscription
                              }
                              onKeyDown={handleKeyDown}
                              summary={
                                frequencyError || !frequencyOptions?.length ? (
                                  <span className={classes.subscriptionError}>
                                    <ErrorCircleIcon
                                      className={classes.errorIcon}
                                    />
                                    <FormattedMessage
                                      id="SUBSCRIPTION_FAILED"
                                      defaultMessage="Subscriptions are currently unavailable."
                                    />
                                  </span>
                                ) : (
                                  <Box className={classes.summaryContainer}>
                                    <Box className={classes.summaryLabel}>
                                      <CommonRadio
                                        label="Subscription Order"
                                        name="defaultName"
                                        size="medium"
                                        value="defaultValue"
                                        onChange={() =>
                                          handleOrderTypeChange(true)
                                        }
                                        checked={subscription}
                                        disabled={isSubscriptionDisabled}
                                      />
                                      <NewTag />
                                    </Box>
                                    {promoType === PromoType.SubscribeSave && (
                                      <div>
                                        <Typography
                                          className={classes.subsSaveMessage}
                                        >
                                          <FormattedMessage
                                            id="SUBSCRIPTION_SAVE_MESSAGE"
                                            defaultMessage="Save {discount}% off list price"
                                            values={{ discount: promoDiscount }}
                                          />
                                        </Typography>
                                      </div>
                                    )}
                                    <ClickAwayListener
                                      onClickAway={handleTooltipClose}
                                    >
                                      <Tooltip
                                        arrow
                                        classes={{
                                          tooltip: classes.tooltip,
                                          arrow: classes.arrow,
                                        }}
                                        onClose={handleTooltipClose}
                                        open={openTooltip}
                                        disableFocusListener
                                        disableHoverListener
                                        disableTouchListener
                                        title={
                                          <>
                                            <Typography
                                              className={classes.tooltipHeading}
                                              variant="h3"
                                            >
                                              <FormattedMessage
                                                id="SUBSCRIPTION_ORDER_HEADING"
                                                defaultMessage="Subscription Orders"
                                              />
                                            </Typography>
                                            {promoType &&
                                            promoType ===
                                              PromoType.SubscribeSave ? (
                                              <Typography variant="body2">
                                                <FormattedMessage
                                                  id="SUBSCRIPTION_LEARN_MORE_DESCRIPTION"
                                                  defaultMessage="Automatically places orders for products at regular intervals ensuring consistent delivery, so you never run out.{br}
                                                    Subscribe for {discount}% off list price on every subscription order or keep your contractual pricing to guarantee you always get the best deal. Price updated in cart. {br}
                                                    Subscriptions can be cancelled anytime from My Account."
                                                  values={{
                                                    br: (
                                                      <>
                                                        <br />
                                                        <br />
                                                      </>
                                                    ),
                                                    discount: promoDiscount,
                                                  }}
                                                />
                                              </Typography>
                                            ) : (
                                              <Typography variant="body2">
                                                <FormattedMessage
                                                  id="SUBSCRIPTION_LEARN_MORE_DESCRIPTION_SUBS_PRODUCTS"
                                                  defaultMessage="Automatically places orders for products at regular intervals ensuring consistent delivery, so you never run out.
Subscriptions can be cancelled anytime from My Account."
                                                />
                                              </Typography>
                                            )}
                                          </>
                                        }
                                        placement="top"
                                      >
                                        <LinkStyleButton
                                          className={classes.infoLink}
                                          onClick={handleTooltipOpen}
                                        >
                                          <FormattedMessage
                                            id="LEARN_MORE"
                                            defaultMessage="Learn More"
                                          />
                                        </LinkStyleButton>
                                      </Tooltip>
                                    </ClickAwayListener>
                                  </Box>
                                )
                              }
                              summaryAriaControls="subscription-order-content"
                              summaryId="subscription-order-header"
                              overrideClasses={{
                                accordion: cx(
                                  classes.accordion,
                                  classes.accordionBackground
                                ),
                                summary: classes.summary,
                                details: classes.details,
                              }}
                            />
                          </Box>
                        ) : null}

                        {!isSubscriptionOrderingEnabled &&
                        !isMultiCountryUser ? (
                          <>
                            {guestShowLocalDealer || loggedInShowLocalDealer ? (
                              <NonGuestShoppingMessage />
                            ) : (
                              <OneTimeOrder validQty={validQty} />
                            )}
                          </>
                        ) : null}
                      </>
                    )}
                  </>
                )}
                {isPricingModal ? (
                  <Box
                    mt={5}
                    className={cx({
                      [classes.pricingModal]: isPricingModal,
                    })}
                  >
                    {material ? (
                      <AddToListButton
                        materialNumber={material?.materialNumber}
                        materialId={material?.materialId}
                        brand={material?.brand}
                        productName={material?.materialDescription}
                        productId={material?.product}
                        type={material?.type}
                        quantity={values.quantity}
                        isLinkButton
                        classes={{
                          linkStyle: classes.linkStyle,
                        }}
                      />
                    ) : null}
                  </Box>
                ) : null}
              </div>
            )}
            <Box
              mt={isMobileSrpBuyBox ? 0 : 5}
              className={cx({
                [classes.hideLink]: isPricingModal,
              })}
            >
              {material ? (
                <AddToListButton
                  materialNumber={material?.materialNumber}
                  materialId={material?.materialId}
                  brand={material?.brand}
                  productName={material?.materialDescription}
                  productId={material?.product}
                  type={material?.type}
                  quantity={values.quantity}
                  isLinkButton
                  classes={{
                    linkStyle: classes.linkStyle,
                  }}
                />
              ) : null}
            </Box>
          </>
        )
      }}
    </Formik>
  )
}

export default BuyBox
